import { useCallback, useMemo } from 'react'
import {
  collectLabels,
  useFilteredTableState,
} from '@/components/admin/shared/hooks/use-filtered-table-state'
import { PROBLEM_SEARCH_KEYS } from '../../hooks/use-problem-search'
import { QUESTIONS_PER_PAGE } from '../constants'
import type { CoachingQuestion } from '../types'
import type { PersistedFilterState } from './use-session-storage'
import { DEFAULT_FILTER_STATE, problemsTableStore } from './use-session-storage'

interface UseTableFiltersOptions {
  questions: CoachingQuestion[]
  /** When true, persists filter state to session storage */
  persist?: boolean
}

interface UseTableFiltersReturn {
  // Filter state
  globalFilter: string
  setGlobalFilter: (value: string) => void
  difficultyFilter: string | null
  setDifficultyFilter: (value: string | null) => void
  sourceFilter: string | null
  setSourceFilter: (value: string | null) => void
  studyPlanFilter: string[]
  setStudyPlanFilter: (plans: string[]) => void
  tagsFilter: string[]
  setTagsFilter: (tags: string[]) => void
  // Derived data
  filteredQuestions: CoachingQuestion[]
  allStudyPlanNames: string[]
  allTags: string[]
  // Pagination
  visibleCount: number
  loadMore: () => void
  // Global filter function for TanStack Table
  globalFilterFn: (
    row: { getValue: (columnId: string) => unknown },
    columnId: string,
    filterValue: string,
  ) => boolean
}

/** A numeric query is an external id — the problem's own or any source's. */
function matchesExactId(question: CoachingQuestion, id: string): boolean {
  return (
    question.externalId === id ||
    (question.allSources?.some((s) => s.externalId === id) ?? false)
  )
}

function applyQuestionFilters(
  questions: CoachingQuestion[],
  filters: PersistedFilterState,
): CoachingQuestion[] {
  let filtered = questions

  if (filters.difficultyFilter) {
    filtered = filtered.filter(
      (q) => q.difficulty?.name === filters.difficultyFilter,
    )
  }

  if (filters.sourceFilter) {
    filtered = filtered.filter((q) =>
      q.allSources?.length
        ? q.allSources.some((s) => s.source === filters.sourceFilter)
        : q.source === filters.sourceFilter,
    )
  }

  if (filters.studyPlanFilter.length > 0) {
    filtered = filtered.filter((q) =>
      q.studyPlans?.some((sp) => filters.studyPlanFilter.includes(sp.name)),
    )
  }

  // Descriptor tags (problemTags)
  if (filters.tagsFilter.length > 0) {
    filtered = filtered.filter((q) =>
      q.problemTags?.some((t) => filters.tagsFilter.includes(t.name)),
    )
  }

  return filtered
}

/**
 * Custom hook for managing table filter state and derived data.
 * Encapsulates all filter logic for the MasterQuestionsTable.
 * Optionally persists state to session storage when `persist` is true.
 */
export function useTableFilters({
  questions,
  persist = false,
}: UseTableFiltersOptions): UseTableFiltersReturn {
  const {
    filters,
    setFilter,
    filteredRows,
    visibleCount,
    loadMore,
    globalFilterFn,
  } = useFilteredTableState<CoachingQuestion, PersistedFilterState>({
    rows: questions,
    persist,
    store: problemsTableStore,
    pageSize: QUESTIONS_PER_PAGE,
    defaultFilters: DEFAULT_FILTER_STATE,
    searchKeys: PROBLEM_SEARCH_KEYS,
    matchesExactId,
    applyFilters: applyQuestionFilters,
  })

  const setGlobalFilter = useCallback(
    (value: string) => setFilter('globalFilter', value),
    [setFilter],
  )
  const setDifficultyFilter = useCallback(
    (value: string | null) => setFilter('difficultyFilter', value),
    [setFilter],
  )
  const setSourceFilter = useCallback(
    (value: string | null) => setFilter('sourceFilter', value),
    [setFilter],
  )
  const setStudyPlanFilter = useCallback(
    (plans: string[]) => setFilter('studyPlanFilter', plans),
    [setFilter],
  )
  const setTagsFilter = useCallback(
    (tags: string[]) => setFilter('tagsFilter', tags),
    [setFilter],
  )

  const allStudyPlanNames = useMemo(
    () =>
      collectLabels(questions, (q) =>
        (q.studyPlans ?? []).map((sp) => sp.name),
      ),
    [questions],
  )

  const allTags = useMemo(
    () =>
      collectLabels(questions, (q) => (q.problemTags ?? []).map((t) => t.name)),
    [questions],
  )

  return {
    globalFilter: filters.globalFilter,
    setGlobalFilter,
    difficultyFilter: filters.difficultyFilter,
    setDifficultyFilter,
    sourceFilter: filters.sourceFilter,
    setSourceFilter,
    studyPlanFilter: filters.studyPlanFilter,
    setStudyPlanFilter,
    tagsFilter: filters.tagsFilter,
    setTagsFilter,
    filteredQuestions: filteredRows,
    allStudyPlanNames,
    allTags,
    visibleCount,
    loadMore,
    globalFilterFn,
  }
}
